import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import _ from "lodash";
import styled, { css } from "styled-components";

import { Breadcrumb, Button, Container, Frame, RowWrapper, TExtra } from "components/ui-kit/styled-templates";
import Tooltip from "components/ui-kit/tooltip";

import DormitoriesApi from "api/dormitories-api";

import ModalsService from "services/modals-service";

import useBodyBackground from "hooks/useBodyBackground";
import useStepsOptions from "hooks/useStepsOptions";
import { useStorageListener } from "hooks/useStorage";

import { ESteps } from "types/enums";

const DormitoriesPage = () => {
    const { t } = useTranslation();

    useBodyBackground(`white`);
    const navigate = useNavigate();

    const dormitories = useStorageListener(
        (state) => _.values(state?.dormitories)?.filter?.((i: any) => i?.universityId === state?.myUniversityId) ?? [],
    );

    const handlers = {
        createDormitory: async () => {
            const { id } = await DormitoriesApi.createDormitory();
            navigate(`/dormitories/${id}`);
        },
        deleteDormitory: (id: string) => async () => {
            ModalsService.showModality({
                title: `Удаление общежития`,
                description: `Вы уверены, что хотите удалить общежитие "${_.find(dormitories, { id })?.details?.name ?? `без названия`}"?`,
                cancelButton: {
                    children: `Отмена`,
                    background: `gray`,
                    onClick: () => {},
                },
                confirmButton: {
                    children: `Удалить`,
                    background: `red`,
                    onClick: async () => await DormitoriesApi.deleteDormitory(id),
                },
            });
        },
    };

    // @ts-ignore
    useEffect(DormitoriesApi.getMyDormitories, []);

    return (
        <Container extra={`align-items: flex-start;`}>
            <Breadcrumb.Wrapper>
                <Breadcrumb.Item to="/">{t("qys77ygwrz")}</Breadcrumb.Item>
            </Breadcrumb.Wrapper>
            <RowWrapper extra={`h1 { margin: 0; };`}>
                <h1>{t("s8kq2tqplzl")}</h1>
                <Button variant="outlined" onClick={handlers.createDormitory}>
                    {t("3xbykd5ter6")}
                </Button>
            </RowWrapper>
            <RowWrapper extra={`margin-top: 56px; flex-wrap: wrap; gap: 40px;`}>
                {dormitories?.map?.(({ details: item = {}, id, onModeration }: any, index: any) => (
                    <DormitoryTab.Component key={index} {...{ item, id, handlers, onModeration }} />
                ))}
            </RowWrapper>
            {!dormitories.length && <NotFoundWrapper>{t("3085txvws7m")}</NotFoundWrapper>}
        </Container>
    );
};

enum EDormitoryTabStatus {
    Accept = `accept`,
    Reject = `reject`,
    Process = `process`,
    None = `none`,
    Verification = `verification`,
}

const tooltipStatusText = {
    [EDormitoryTabStatus.Accept]: `Все данные успешно заполнены, проверка пройдена`,
    [EDormitoryTabStatus.Verification]: `Все данные успешно заполнены, ожидает проверки`,
    [EDormitoryTabStatus.Reject]: `Ожидает заполнения`,
    [EDormitoryTabStatus.Process]: `Ожидает заполнения`,
    [EDormitoryTabStatus.None]: `Ожидает заполнения`,
};

export const DormitoryTab = {
    Component: (props: { item: any; id: string; handlers: { deleteDormitory: Function }; onModeration: boolean }) => {
        const { t } = useTranslation();

        const { item, id, handlers, onModeration } = props;

        const { allDone, allUndone, progress } = useStepsOptions(item);

        const status = allDone
            ? onModeration
                ? EDormitoryTabStatus.Verification
                : EDormitoryTabStatus.Accept
            : allUndone
            ? EDormitoryTabStatus.Reject
            : EDormitoryTabStatus.Process;

        return (
            <DormitoryTab.Wrapper>
                <div className="flex flex-row w-100 justify-between items-center">
                    <Tooltip
                        label={`${tooltipStatusText[status]}${
                            ![EDormitoryTabStatus.Accept, EDormitoryTabStatus.Verification].includes(status)
                                ? ` (${_.values(progress).filter((i) => i).length}/${_.values(progress).length} шагов выполнено)`
                                : ``
                        }`}
                        wrapperProps={{ extra: `position: absolute; top: 10px; right: 10px;` }}
                    >
                        <DormitoryTab.WarningSign status={status} />
                    </Tooltip>
                </div>
                <DormitoryTab.Name empty={_.isEmpty(item?.[ESteps.MainInfo]?.name)}>{item?.[ESteps.MainInfo]?.name ?? `Без имени`}</DormitoryTab.Name>
                <Link to={`/dormitories/${id}`}>
                    <DormitoryTab.Link color="blue">{t("uklgxwgdkle")}</DormitoryTab.Link>
                </Link>
                <Link to={`/dormitory/${id}`}>
                    <DormitoryTab.Link color="blue">{t("xjbm05xj2fe")}</DormitoryTab.Link>
                </Link>
                <DormitoryTab.Link color="red" onClick={handlers.deleteDormitory(id)}>
                    {t("vutdi0fht5o")}
                </DormitoryTab.Link>
            </DormitoryTab.Wrapper>
        );
    },
    WarningSign: styled(Frame)<{ status?: EDormitoryTabStatus }>`
        width: 24px;
        height: 24px;
        background: url("${({ status = EDormitoryTabStatus.None }) => require(`assets/icons/dormitory-status-${status}.svg`)}") no-repeat center
            center / contain;
    `,
    Wrapper: styled(Frame)<{ status?: EDormitoryTabStatus }>`
        width: calc(50% - 20px);
        padding: 24px 20px;
        box-sizing: border-box;
        border: 1px solid ${({ theme }) => theme.halfblue};
        border-radius: 8px;
        align-items: flex-start;
        justify-content: flex-start;
        gap: 10px 0;
        position: relative;

        > * {
            margin: 0;
        }

        @media (max-width: 991px) {
            width: inherit;
        }
    `,
    Name: styled.h3<{ empty: boolean; extra?: TExtra }>`
        color: ${({ theme, empty }) => (empty ? theme.text.light : theme.text.primary)};

        ${({ extra }) => extra}
    `,
    Link: styled(Frame)<{ color: `red` | `blue`; direction?: `right` | `left` }>`
        color: ${({ theme, color }) => theme[color]};
        font-weight: 600;
        font-size: 16px;
        line-height: 20px;
        display: flex;
        align-items: center;
        flex-direction: row;
        cursor: pointer;

        ${({ direction = `right` }) => (direction === `right` ? `&:after` : `&:before`)} {
            content: "";
            width: 12px;
            height: 12px;
            background: url("${({ color }) => require(`assets/icons/arrow-right-${color}.svg`)}") no-repeat center center / contain;
            margin-left: 8px;
            transition: 0.2s;

            ${({ direction = `right` }) =>
                direction === `left` &&
                css`
                    transform: rotate(180deg);
                    margin-left: unset;
                    margin-right: 8px;
                `}
        }

        &:hover {
            ${({ direction = `right` }) => (direction === `right` ? `&:after` : `&:before`)} {
                transform: translateX(${({ direction = `right` }) => (direction === `right` ? 3 : -3)}px)
                    ${({ direction = `right` }) => (direction === `left` ? `rotate(180deg)` : ``)};
            }
        }
    `,
    DetailsWrapper: styled(RowWrapper)`
        > * {
            margin-bottom: 8px;
            p {
                margin-bottom: 8px;
            }
        }

        @media (max-width: 991px) {
            flex-wrap: wrap;
            > * {
                width: 50%;
            }
        } ;
    `,
};

export const NotFoundWrapper = styled(Frame)`
    width: inherit;
    color: ${({ theme }) => theme.grey};
    padding: 40px 0;
`;

export default DormitoriesPage;
